// src/composables/useToast.js
// App-wide toast singleton for the desktop shell. State lives at module scope
// so any view can push a toast and <ToastHost> (mounted once in App.vue)
// renders the shared stack. Mirrors the mobile composable's API
// (showToast(type, message)) but keeps a stack of up to 3 toasts, each with
// its own auto-dismiss timer. Do not use in the mobile app — it has its own
// per-view composable at src/mobile/composables/useToast.js.
//
// Usage:
//   const { showToast } = useToast()
//   showToast('success', 'Order deleted')   // type: 'success'|'error'|'info'

import { ref } from "vue";

const DURATION = 4500;
const MAX_TOASTS = 3;

const toasts = ref([]); // [{ id, type, message }]
const timers = new Map();
let nextId = 0;

function dismissToast(id) {
  const timer = timers.get(id);
  if (timer) {
    clearTimeout(timer);
    timers.delete(id);
  }
  toasts.value = toasts.value.filter((t) => t.id !== id);
}

function showToast(type, message) {
  const id = ++nextId;
  toasts.value = [...toasts.value, { id, type, message }];
  // Cap the stack by dropping the oldest toast (and its timer).
  while (toasts.value.length > MAX_TOASTS) {
    dismissToast(toasts.value[0].id);
  }
  timers.set(
    id,
    setTimeout(() => dismissToast(id), DURATION)
  );
}

export default function useToast() {
  return { toasts, showToast, dismissToast };
}
